import React, { CSSProperties, useEffect } from 'react';
import useTranslate from '../../useTranslate';
import { Link, useLocation } from 'react-router-dom';
import { Col, FormControl, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import ValidatorFormGroup from '../../main/validator-form-group';
import EditLargeCardFormik, {
    EditLargeCardFormikProps,
} from '../../main/edit-large-card-formik';
import { connect } from 'react-redux';
import { RootState } from '../../../store';
import {
    BaseLocationCostsDto,
    LoadLocationCosts,
    LocationCostsDto,
    ResetSetLocationCosts,
    RevokeScheduledCostsChange,
    Schedule,
    SetLocationCosts,
    SetLocationCostsDto,
} from '../../../store/locationCostsSlice';
import { RequestStatus } from '../../../store/network/managedRequest';
import Spinner from '../../util/spinner';
import { FormikErrors } from 'formik';
import TimelineBox, { FancyDotPosition } from './TimelineBox';
import ScheduleTimeSelect from './schedule-time-select';
import { PbGenericRequest } from '../../../store/pbRequestSlice';
import { PbRequest } from '../../../store/pbRequest';

const PRICES_UNCHANGED = 'PRICES_UNCHANGED';

enum VipPricesAction {
    KEEP = 'KEEP',
    CHANGE = 'CHANGE',
    DISABLE = 'DISABLE',
}

interface Props {
    locationId: number;
    hideLinkToDiscount?: boolean;
    disabled?: boolean;

    LoadLocationCosts: (locationId: number) => void;
    SetLocationCosts: (locationId: number, dto: SetLocationCostsDto) => void;
    RevokeScheduledCostsChange: (locationId: number) => void;
    ResetSetLocationCosts: () => void;

    locationCostsRequest: PbGenericRequest;
    locationCostsData: LocationCostsDto;
    setLocationCostsRequest: PbGenericRequest;
}

type LaundryRoomPricesFormFieldValues = {
    [key in LaundryRoomPricesFormFields]: string;
};
export type LaundryRoomPricesFormValues = LaundryRoomPricesFormFieldValues & {
    use_vip_prices: boolean;
    vip_prices_action: VipPricesAction;
    schedule_type: Schedule;
};

export enum LaundryRoomPricesFormFields {
    price_vip_dryer = 'price_vip_dryer',
    price_vip_washing_machine = 'price_vip_washing_machine',
    price_normal_dryer = 'price_normal_dryer',
    price_normal_washing_machine = 'price_normal_washing_machine',
}

function LaundryRoomPricesCard(props: Readonly<Props>) {
    const { pathname } = useLocation();

    useEffect(() => {
        props.ResetSetLocationCosts();
        props.LoadLocationCosts(props.locationId);
    }, [props.locationId]);

    const strings = useTranslate('LaundryRoomPricesCard');

    if (
        [RequestStatus.INITIAL, RequestStatus.PENDING].includes(
            props.locationCostsRequest.status
        )
    ) {
        return <Spinner />;
    }

    const locationPriceDto: LocationCostsDto = props.locationCostsData;
    if (!locationPriceDto) {
        return <div>{strings.loadingError}</div>;
    }
    const currencySymbol = strings._utils.getCurrencySymbol(
        locationPriceDto.currency_code
    );
    const vipPricesAllowed = locationPriceDto.vip_costs_enabled;
    const vipPricesInUse =
        locationPriceDto.price_vip_dryer != undefined ||
        locationPriceDto.price_vip_washing_machine != undefined;

    const showLinkToVip =
        !props.hideLinkToDiscount && locationPriceDto.vip_costs_enabled;

    const pricesToShow: BaseLocationCostsDto = locationPriceDto.scheduled_costs
        ? locationPriceDto.scheduled_costs
        : locationPriceDto;

    const vipPricesInUseFuture =
        pricesToShow.price_vip_dryer != undefined ||
        pricesToShow.price_vip_washing_machine != undefined;

    const vipPricesDifferFromCurrentPrices =
        pricesToShow.price_vip_dryer !== locationPriceDto.price_vip_dryer ||
        pricesToShow.price_vip_washing_machine !==
            locationPriceDto.price_vip_washing_machine;

    let action: VipPricesAction;
    if (!vipPricesInUseFuture) {
        action = VipPricesAction.DISABLE;
    } else if (vipPricesDifferFromCurrentPrices) {
        action = VipPricesAction.CHANGE;
    } else {
        action = VipPricesAction.KEEP;
    }

    const updateFailed =
        props.setLocationCostsRequest.status === RequestStatus.FAILURE;

    const initialValues: LaundryRoomPricesFormValues = {
        use_vip_prices:
            vipPricesAllowed &&
            (pricesToShow.price_vip_dryer != undefined ||
                pricesToShow.price_vip_washing_machine != undefined),
        vip_prices_action: action,
        schedule_type: Schedule.IN_SEVEN_DAYS,
        [LaundryRoomPricesFormFields.price_normal_dryer]:
            strings._utils.formatNumber(pricesToShow.price_normal_dryer, 2),
        [LaundryRoomPricesFormFields.price_vip_dryer]:
            strings._utils.formatNumber(pricesToShow.price_vip_dryer, 2),
        [LaundryRoomPricesFormFields.price_normal_washing_machine]:
            strings._utils.formatNumber(
                pricesToShow.price_normal_washing_machine,
                2
            ),
        [LaundryRoomPricesFormFields.price_vip_washing_machine]:
            strings._utils.formatNumber(
                pricesToShow.price_vip_washing_machine,
                2
            ),
    };

    const labels = {
        [LaundryRoomPricesFormFields.price_normal_dryer]:
            strings._utils.sprintf(strings.drying, currencySymbol),
        [LaundryRoomPricesFormFields.price_vip_dryer]: strings._utils.sprintf(
            strings.dryingVip,
            currencySymbol
        ),
        [LaundryRoomPricesFormFields.price_normal_washing_machine]:
            strings._utils.sprintf(strings.washing, currencySymbol),
        [LaundryRoomPricesFormFields.price_vip_washing_machine]:
            strings._utils.sprintf(strings.washingVip, currencySymbol),
    };

    function handlePriceSubmit(values: LaundryRoomPricesFormValues) {
        const useVipPrices =
            values.vip_prices_action !== VipPricesAction.DISABLE;

        const vipDryerPrice =
            values.vip_prices_action === VipPricesAction.KEEP
                ? locationPriceDto.price_vip_dryer
                : parseAsPrice(
                      values[LaundryRoomPricesFormFields.price_vip_dryer]
                  );

        const vipWashingMachinePrice =
            values.vip_prices_action === VipPricesAction.KEEP
                ? locationPriceDto.price_vip_washing_machine
                : parseAsPrice(
                      values[
                          LaundryRoomPricesFormFields.price_vip_washing_machine
                      ]
                  );

        const writeValues: SetLocationCostsDto = {
            use_vip_prices: useVipPrices,
            schedule: values.schedule_type,
            [LaundryRoomPricesFormFields.price_normal_dryer]: parseAsPrice(
                values[LaundryRoomPricesFormFields.price_normal_dryer]
            ),
            [LaundryRoomPricesFormFields.price_vip_dryer]: useVipPrices
                ? vipDryerPrice
                : undefined,
            [LaundryRoomPricesFormFields.price_normal_washing_machine]:
                parseAsPrice(
                    values[
                        LaundryRoomPricesFormFields.price_normal_washing_machine
                    ]
                ),
            [LaundryRoomPricesFormFields.price_vip_washing_machine]:
                useVipPrices ? vipWashingMachinePrice : undefined,
        };
        props.SetLocationCosts(props.locationId, writeValues);
    }

    function handleRemove() {
        props.RevokeScheduledCostsChange(props.locationId);
    }

    return (
        <EditLargeCardFormik
            title={strings.title}
            handleSubmit={handlePriceSubmit}
            initialValues={initialValues}
            validate={validateFormValues}
            error={updateFailed}
            thirdButton={!!locationPriceDto.scheduled_costs}
            thirdButtonLabel={strings.buttonRemove}
            handleThirdButton={handleRemove}
            submitLabel={strings.buttonSave}
            cancelLabel={strings.buttonCancel}
            handleCancel={props.ResetSetLocationCosts}
            enableReinitialize
            disabled={props.disabled}
        >
            {renderForm}
        </EditLargeCardFormik>
    );

    function renderForm(
        formProps: EditLargeCardFormikProps<LaundryRoomPricesFormValues>
    ) {
        const showUpcomingPrices =
            formProps.active || !!locationPriceDto.scheduled_costs;

        return (
            <Col xs={12}>
                {renderFixedPart()}
                {showUpcomingPrices && <div className={'timeline-connector'} />}
                {showUpcomingPrices && renderUpcomingPart()}
                {showUpcomingPrices && !formProps.active && (
                    <Row className={'mb-2'}>
                        <Col xs={12}>{strings.scheduledInfoText}</Col>
                    </Row>
                )}
                {showLinkToVip && (
                    <Row>
                        <Col xs={12}>{renderVipLink()}</Col>
                    </Row>
                )}
                {updateFailed && (
                    <Row>
                        <Col xs={12}>
                            <div className="alert alert-danger">
                                {getErrorMessage(
                                    props.setLocationCostsRequest.errors
                                )}
                            </div>
                        </Col>
                    </Row>
                )}
            </Col>
        );

        function getErrorMessage(tags: string[]): string {
            return tags.includes(PRICES_UNCHANGED)
                ? strings.pricesUnchangedError
                : strings.genericError;
        }

        function renderFixedPart() {
            return (
                <TimelineBox
                    hidden={!showUpcomingPrices}
                    dotPosition={FancyDotPosition.BOTTOM}
                    title={strings.boxTitleCurrent}
                    dotText={strings.boxInfoNow}
                >
                    <Row>
                        <Col xs={6}>
                            {renderFixedField(
                                LaundryRoomPricesFormFields.price_normal_washing_machine
                            )}
                        </Col>
                        <Col xs={6}>
                            {renderFixedField(
                                LaundryRoomPricesFormFields.price_normal_dryer
                            )}
                        </Col>
                    </Row>
                    {vipPricesInUse && (
                        <Row>
                            <Col xs={6}>
                                {renderFixedField(
                                    LaundryRoomPricesFormFields.price_vip_washing_machine
                                )}
                            </Col>
                            <Col xs={6}>
                                {renderFixedField(
                                    LaundryRoomPricesFormFields.price_vip_dryer
                                )}
                            </Col>
                        </Row>
                    )}
                </TimelineBox>
            );
        }

        function renderUpcomingPart() {
            const scheduleHint =
                formProps.values.schedule_type === Schedule.IMMEDIATELY
                    ? strings.scheduleHintImmediately
                    : strings.scheduleHintDelayed;

            return (
                <TimelineBox
                    dotPosition={FancyDotPosition.TOP}
                    title={strings.boxTitleFuture}
                    dotText={strings._utils.sprintf(
                        strings.boxInfoFuture,
                        getTimeString()
                    )}
                >
                    {formProps.active && (
                        <Row className={'mb-2'}>
                            <Col xs={12}>{renderPriceCalculatorLink()}</Col>
                        </Row>
                    )}
                    <Row>
                        <Col xs={6}>
                            {renderEditField(
                                LaundryRoomPricesFormFields.price_normal_washing_machine
                            )}
                        </Col>
                        <Col xs={6}>
                            {renderEditField(
                                LaundryRoomPricesFormFields.price_normal_dryer
                            )}
                        </Col>
                    </Row>
                    {vipPricesAllowed && (
                        <Row>
                            <Col xs={12}>{renderVipPricesAction()}</Col>
                        </Row>
                    )}
                    {(formProps.values.vip_prices_action ===
                        VipPricesAction.CHANGE ||
                        (!formProps.active && vipPricesInUseFuture)) && (
                        <Row>
                            <Col xs={6}>
                                {renderEditField(
                                    LaundryRoomPricesFormFields.price_vip_washing_machine
                                )}
                            </Col>
                            <Col xs={6}>
                                {renderEditField(
                                    LaundryRoomPricesFormFields.price_vip_dryer
                                )}
                            </Col>
                        </Row>
                    )}
                    {formProps.active && (
                        <Row>
                            <Col xs={12}>
                                <ScheduleTimeSelect
                                    label={strings.scheduleLabel}
                                    hint={scheduleHint}
                                    value={formProps.values.schedule_type}
                                    onChange={formProps.handleChange}
                                />
                            </Col>
                        </Row>
                    )}
                </TimelineBox>
            );
        }

        function renderVipPricesAction() {
            if (!formProps.active) {
                return null;
            }

            return (
                <div className="mb-2">
                    {vipPricesInUse && (
                        <div className="radio radio-primary">
                            <input
                                type="radio"
                                id="vip_prices_keep"
                                checked={
                                    formProps.values.vip_prices_action ===
                                    VipPricesAction.KEEP
                                }
                                onChange={formProps.handleChange}
                                name="vip_prices_action"
                                value={VipPricesAction.KEEP}
                            />
                            <label htmlFor="vip_prices_keep">
                                {strings.vipPricesKeep}
                            </label>
                        </div>
                    )}
                    <div className="radio radio-primary">
                        <input
                            type="radio"
                            id="vip_prices_change"
                            checked={
                                formProps.values.vip_prices_action ===
                                VipPricesAction.CHANGE
                            }
                            onChange={formProps.handleChange}
                            name="vip_prices_action"
                            value={VipPricesAction.CHANGE}
                        />
                        <label htmlFor="vip_prices_change">
                            {vipPricesInUse
                                ? strings.vipPricesChange
                                : strings.vipPricesConfigure}
                        </label>
                    </div>
                    <div className="radio radio-primary">
                        <input
                            type="radio"
                            id="vip_prices_disable"
                            checked={
                                formProps.values.vip_prices_action ===
                                VipPricesAction.DISABLE
                            }
                            onChange={formProps.handleChange}
                            name="vip_prices_action"
                            value={VipPricesAction.DISABLE}
                        />
                        <label htmlFor="vip_prices_disable">
                            {vipPricesInUse
                                ? strings.vipPricesDisable
                                : strings.vipPricesDoNotConfigure}
                        </label>
                    </div>
                    <div style={fontStyle}>{getVipPricesActionHint()}</div>
                </div>
            );
        }

        function getVipPricesActionHint(): string {
            switch (formProps.values.vip_prices_action) {
                case VipPricesAction.KEEP:
                    return strings.vipPricesKeepHint;
                case VipPricesAction.CHANGE:
                    return strings.vipPricesChangeHint;
                case VipPricesAction.DISABLE:
                    return vipPricesInUse
                        ? strings.vipPricesDisableHint
                        : strings.vipPricesDoNotConfigureHint;
            }
        }

        function getTimeString(): string {
            const locale =
                strings._currentLanguage === 'ger' ? 'de-DE' : 'en-GB';

            if (formProps.active) {
                const futureTimestamp = getTimestampFromSchedule(
                    formProps.values.schedule_type
                );
                return new Date(futureTimestamp).toLocaleDateString(locale);
            } else {
                const futureTimestamp =
                    locationPriceDto.scheduled_costs?.due_time || Date.now();
                return new Date(futureTimestamp).toLocaleString(locale);
            }
        }

        function renderFixedField(field: LaundryRoomPricesFormFields) {
            return (
                <ValidatorFormGroup label={labels[field]} hideErrorLabel>
                    <FormControl
                        type="text"
                        className="input-text-color"
                        value={strings._utils.formatNumber(
                            locationPriceDto[field],
                            2
                        )}
                        disabled
                    />
                </ValidatorFormGroup>
            );
        }

        function renderEditField(field: LaundryRoomPricesFormFields) {
            return (
                <ValidatorFormGroup
                    label={labels[field]}
                    hideErrorLabel={!formProps.errors[field]}
                    errorLabel={strings.error}
                >
                    <FormControl
                        type="text"
                        className="input-text-color"
                        value={formProps.values[field]}
                        required
                        name={field}
                        onChange={formProps.handleChange}
                        disabled={!formProps.active}
                    />
                </ValidatorFormGroup>
            );
        }

        function renderVipLink() {
            if (formProps.active) {
                return null;
            }

            const last = pathname.lastIndexOf('/');
            const basicPath = pathname.substring(0, last);

            return (
                <div style={{ textAlign: 'end' }}>
                    <Link to={`${basicPath}/vip-user-pricing`}>
                        {strings.configureVipSettings}
                    </Link>
                </div>
            );
        }

        function renderPriceCalculatorLink() {
            return (
                <div style={fontStyle}>
                    <a href={strings.costCalculatorLink} target={'_blank'}>
                        <FontAwesomeIcon icon={faInfoCircle} size={'1x'} />{' '}
                        {strings.costCalculatorAction}
                    </a>
                </div>
            );
        }
    }
}

function validateFormValues(values: LaundryRoomPricesFormValues) {
    const error: FormikErrors<LaundryRoomPricesFormValues> = {};
    for (const key in LaundryRoomPricesFormFields) {
        if (Number.isNaN(parseAsPrice(values[key]))) {
            error[key] = 'nan';
        }
    }
    if (values.vip_prices_action !== VipPricesAction.CHANGE) {
        delete error[LaundryRoomPricesFormFields.price_vip_washing_machine];
        delete error[LaundryRoomPricesFormFields.price_vip_dryer];
    }
    return error;
}

function getTimestampFromSchedule(scheduleType: Schedule) {
    switch (scheduleType) {
        case Schedule.IMMEDIATELY:
            return Date.now();
        case Schedule.FIRST_OF_NEXT_MONTH: {
            const nextMonth = new Date();
            nextMonth.setMonth(nextMonth.getMonth() + 1, 1);
            return nextMonth.getTime();
        }
        case Schedule.IN_SEVEN_DAYS: {
            const inDays = new Date();
            inDays.setDate(inDays.getDate() + 7);
            return inDays.getTime();
        }
    }
    return Date.now();
}

function parseAsPrice(str: string): number {
    // WARNING: this ignores the possibility of use comma as thousand separator
    return Number.parseFloat(str.trim().replace(',', '.'));
}

const fontStyle: CSSProperties = { fontStyle: 'italic' };
export default connect(
    (store: RootState) => ({
        locationCostsRequest: store.pbRequest[PbRequest.GET_LOCATION_COSTS],
        locationCostsData: store.locationCosts.locationCosts,
        setLocationCostsRequest: store.pbRequest[PbRequest.SET_LOCATION_COSTS],
    }),
    {
        LoadLocationCosts,
        SetLocationCosts,
        RevokeScheduledCostsChange,
        ResetSetLocationCosts,
    }
)(LaundryRoomPricesCard);
